import { useState, useEffect } from "react";
import FileDrop from "senf-atomic-design-system/src/components/core/fileDrop/FileDrop";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";

const accessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI1NTlmY2M0Zi00Mzk4LTQ1NDUtYjNjMi1iMmE4YzUwMjU3MDIiLCJpZCI6MTk0OTI5LCJpYXQiOjE3NDkxOTk1Njd9.aXbwP3aBfaoGGJwiJh3mwoIdZeL9BPag8kaNhZ5yGXs";

// Helper function to convert string to Uint8Array
const stringToUint8Array = (str: string) => new TextEncoder().encode(str);

// Helper function to convert ArrayBuffer to hex string
const arrayBufferToHex = (buffer: ArrayBuffer) => {
  return Array.from(new Uint8Array(buffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
};

// Helper function to create HMAC-SHA256 signature
async function hmacSha256(key: string, message: string): Promise<string> {
  const keyData = stringToUint8Array(key);
  const messageData = stringToUint8Array(message);
  
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
  return arrayBufferToHex(signature);
}

// Helper function to create SHA-256 hash
async function sha256(message: string): Promise<string> {
  const messageData = stringToUint8Array(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', messageData);
  return arrayBufferToHex(hashBuffer);
}

interface AssetUpload {
  file: File;
  assetId: number | null;
  assetName: string;
  progress: number;
  showLoader: boolean;
  tilingStatus: string;
  isTiling: boolean;
  tilingPercent: number;
  uploadStatus: string;
  fileSize: string;
  fileSizeBytes: number;
  startTime: Date;
  uploadSpeed?: number; // bytes per second
  estimatedTotal?: number; // estimated total time in seconds
  currentPhase: 'preparing' | 'uploading' | 'processing' | 'complete' | 'error';
  uploadStartTime?: Date;
  bytesUploaded?: number;
  detectedEpsg?: string; // detected coordinate system
}

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to format speed
const formatSpeed = (bytesPerSecond: number): string => {
  return formatFileSize(bytesPerSecond) + '/s';
};

// Helper function to estimate upload time based on file size
const estimateUploadTime = (fileSizeBytes: number): number => {
  // Estimate based on average broadband speed (assuming 10 Mbps = 1.25 MB/s)
  const averageSpeedMBps = 1.25 * 1024 * 1024; // bytes per second
  const baseUploadTime = fileSizeBytes / averageSpeedMBps;
  const processingOverhead = Math.min(30, fileSizeBytes / (10 * 1024 * 1024)); // 30s max for large files
  return baseUploadTime + processingOverhead;
};

// Helper function to get progress bar color based on file size and speed
const getProgressBarColor = (fileSizeBytes: number, uploadSpeed?: number): string => {
  if (uploadSpeed && uploadSpeed > 5 * 1024 * 1024) return 'bg-green-500'; // Fast upload
  if (fileSizeBytes > 100 * 1024 * 1024) return 'bg-orange-500'; // Large file
  if (fileSizeBytes > 50 * 1024 * 1024) return 'bg-yellow-500'; // Medium file
  return 'bg-blue-500'; // Normal
};

// Helper function to get animation class based on file size
const getProgressAnimation = (fileSizeBytes: number, currentPhase: string): string => {
  if (currentPhase === 'uploading') {
    if (fileSizeBytes > 100 * 1024 * 1024) return 'animate-pulse'; // Large files pulse
    return ''; // Normal animation for smaller files
  }
  if (currentPhase === 'processing') return 'animate-pulse';
  return '';
};

// Helper function to detect EPSG code from file content or name
const detectEpsgCode = async (file: File): Promise<string> => {
  const defaultEpsg = "EPSG:5685"; // DB_REF system - your primary coordinate system
  
  try {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop() || '';
    
    console.log(`🔍 Detecting EPSG code for file: ${file.name} (${formatFileSize(file.size)})`);
    console.log(`📁 File extension: ${fileExtension}`);
    console.log(`🏷️ Full filename (lowercase): ${fileName}`);
    
    // First check filename for EPSG patterns (most reliable)
    const epsgPatterns = [
      /epsg[_\-\s:]*(\d+)/i,
      /crs[_\-\s:]*(\d+)/i,
      /srid[_\-\s:]*(\d+)/i,
      /utm[_\-\s]*(\d+)/i,
      /zone[_\-\s]*(\d+)/i
    ];
    
    for (const pattern of epsgPatterns) {
      const match = fileName.match(pattern);
      if (match) {
        const code = match[1];
        // Convert UTM zone to EPSG
        if (pattern.source.includes('utm') || pattern.source.includes('zone')) {
          const utmZone = parseInt(code);
          if (utmZone >= 1 && utmZone <= 60) {
            const epsgCode = utmZone <= 30 ? `EPSG:${32600 + utmZone}` : `EPSG:${32600 + utmZone}`;
            console.log(`🗺️ Detected UTM Zone ${utmZone} → ${epsgCode}`);
            return epsgCode;
          }
        } else {
          const detectedEpsg = `EPSG:${code}`;
          console.log(`✅ Detected EPSG code from filename pattern: ${detectedEpsg}`);
          return detectedEpsg;
        }
      }
    }
    
         // Enhanced location hints with more patterns - prioritizing your project coordinate systems
     const locationHints = {
       // Primary project coordinate systems
       'db_ref': 'EPSG:5685',
       'dbref': 'EPSG:5685',
       'db-ref': 'EPSG:5685',
       's_jtsk': 'EPSG:5514',
       'sjtsk': 'EPSG:5514',
       's-jtsk': 'EPSG:5514',
       'jtsk': 'EPSG:5514',
       
       // Germany & Berlin (prefer EPSG:5685 for your projects)
       'berlin': 'EPSG:5685',
       'germany': 'EPSG:5685',
       'deutschland': 'EPSG:5685',
       'german': 'EPSG:5685',
       'de': 'EPSG:5685',
       
       // Czech Republic (EPSG:5514)
       'czech': 'EPSG:5514',
       'czechia': 'EPSG:5514',
       'czech_republic': 'EPSG:5514',
       'ceska': 'EPSG:5514',
       'cz': 'EPSG:5514',
       'praha': 'EPSG:5514',
       'prague': 'EPSG:5514',
       
       // Cross-border projects
       'de_cz': 'EPSG:5685', // Default to German system for cross-border
       'cz_de': 'EPSG:5514', // Default to Czech system if Czech comes first
       'border': 'EPSG:5685',
       'grenze': 'EPSG:5685',
       
       // UTM zones for Germany (fallback)
       'utm32': 'EPSG:25832',
       'utm33': 'EPSG:25833',
       'utm32n': 'EPSG:25832',
       'utm33n': 'EPSG:25833',
       
       // ETRS89 (fallback)
       'etrs89': 'EPSG:25833',
       'etrs': 'EPSG:25833',
       
       // Common systems
       'wgs84': 'EPSG:4326',
       'wgs': 'EPSG:4326',
       'mercator': 'EPSG:3857',
       'webmercator': 'EPSG:3857',
       
       // European systems
       'lambert': 'EPSG:2154',
       'rd': 'EPSG:28992', // Netherlands
       'british': 'EPSG:27700', // UK
       'swiss': 'EPSG:2056', // Switzerland
     };
    
    console.log(`🔍 Checking location hints in filename...`);
    for (const [hint, epsg] of Object.entries(locationHints)) {
      if (fileName.includes(hint)) {
        console.log(`🎯 ✅ MATCH! Found location hint "${hint}" → ${epsg}`);
        return epsg;
      } else {
        console.log(`🔍 No match for hint "${hint}"`);
      }
    }
    
    // Try to read file content for text-based files
    if (['ifc', 'txt', 'prj', 'xml', 'json'].includes(fileExtension) && file.size < 50 * 1024 * 1024) {
      console.log(`📖 Reading file content for EPSG detection...`);
      
      try {
        const text = await file.text();
        const textSample = text.substring(0, 10000); // First 10KB
        console.log(`📄 Text sample length: ${textSample.length} characters`);
        
                 // Look for EPSG patterns in content - prioritizing your coordinate systems
         const contentPatterns = [
           /EPSG[:\s"']*5685/gi,  // Priority: DB_REF
           /EPSG[:\s"']*5514/gi,  // Priority: S-JTSK
           /DB[_\-\s]*REF/gi,     // DB_REF system identifier
           /S[_\-\s]*JTSK/gi,     // S-JTSK system identifier
           /EPSG[:\s"']*(\d+)/gi, // General EPSG detection
           /PROJCS.*?AUTHORITY.*?EPSG.*?(\d+)/gi,
           /IFCPROJECTEDCRS.*?(\d+)/gi,
           /coordinate[_\s]*system.*?(\d+)/gi,
           /CRS.*?(\d+)/gi,
           /SRID[=:\s]*(\d+)/gi
         ];
        
                 for (const pattern of contentPatterns) {
           const matches = textSample.matchAll(pattern);
           for (const match of matches) {
             // Handle priority patterns first
             if (pattern.source.includes('5685')) {
               console.log(`✅ Detected DB_REF coordinate system (EPSG:5685) from file content`);
               return 'EPSG:5685';
             }
             if (pattern.source.includes('5514')) {
               console.log(`✅ Detected S-JTSK coordinate system (EPSG:5514) from file content`);
               return 'EPSG:5514';
             }
             if (pattern.source.includes('DB.*REF')) {
               console.log(`✅ Detected DB_REF system identifier → EPSG:5685`);
               return 'EPSG:5685';
             }
             if (pattern.source.includes('S.*JTSK')) {
               console.log(`✅ Detected S-JTSK system identifier → EPSG:5514`);
               return 'EPSG:5514';
             }
             
             // Handle general EPSG codes
             const code = match[1];
             if (code && code.length >= 4 && code.length <= 5) {
               const detectedEpsg = `EPSG:${code}`;
               console.log(`✅ Detected EPSG code from file content: ${detectedEpsg}`);
               return detectedEpsg;
             }
           }
         }
        
        // Special handling for IFC files
        if (fileExtension === 'ifc') {
          // Look for coordinate reference system in IFC
          const ifcCrsMatch = textSample.match(/IFCGEOGRAPHICREFERENCEINFORMATION.*?(\d+)/i);
          if (ifcCrsMatch) {
            const detectedEpsg = `EPSG:${ifcCrsMatch[1]}`;
            console.log(`🏗️ Detected EPSG from IFC geographic reference: ${detectedEpsg}`);
            return detectedEpsg;
          }
        }
        
      } catch (readError) {
        console.log(`⚠️ Could not read file content (might be binary): ${readError}`);
      }
    }
    
         console.log(`⚠️ No EPSG code detected in filename or content, using default DB_REF system: ${defaultEpsg}`);
     return defaultEpsg;
    
  } catch (error) {
    console.error(`❌ Error detecting EPSG code:`, error);
    console.log(`🔄 Fallback to default EPSG: ${defaultEpsg}`);
    return defaultEpsg;
  }
};

// Helper function to detect EPSG from multiple files (for ZIP uploads with .prj files)
const detectEpsgFromMultipleFiles = async (files: File[]): Promise<string> => {
  console.log(`🔍 Analyzing ${files.length} files for EPSG detection:`);
  files.forEach(f => console.log(`  📁 ${f.name} (${formatFileSize(f.size)})`));
  
  // Look for projection-related files first (highest priority)
  const projectionFiles = files.filter(f => {
    const name = f.name.toLowerCase();
    return name.endsWith('.prj') || name.endsWith('.wkt') || name.endsWith('.crs') || name.includes('projection');
  });
  
  for (const projFile of projectionFiles) {
    try {
      const content = await projFile.text();
      console.log(`📄 Reading projection file: ${projFile.name}`);
      console.log(`📄 Content preview: ${content.substring(0, 300)}...`);
      
      // Enhanced EPSG detection patterns for projection files
      const epsgPatterns = [
        /EPSG[\[\":\s,]*(\d+)/gi,
        /AUTHORITY[\[\":\s,]*EPSG[\[\":\s,]*(\d+)/gi,
        /PROJCS.*?AUTHORITY.*?(\d+)/gi,
        /GEOGCS.*?AUTHORITY.*?(\d+)/gi,
        /SRID[=:\s]*(\d+)/gi,
        /\+init=epsg:(\d+)/gi
      ];
      
      for (const pattern of epsgPatterns) {
        const matches = content.matchAll(pattern);
        for (const match of matches) {
          const code = match[1];
          if (code && code.length >= 4 && code.length <= 5) {
            const detectedEpsg = `EPSG:${code}`;
            console.log(`✅ Detected EPSG code from ${projFile.name}: ${detectedEpsg}`);
            return detectedEpsg;
          }
        }
      }
    } catch (error) {
      console.error(`❌ Error reading projection file ${projFile.name}:`, error);
    }
  }
  
  // Look for coordinate system hints in all filenames first
  console.log(`🔍 Checking all filenames for coordinate system hints...`);
  for (const file of files) {
    const fileName = file.name.toLowerCase();
    console.log(`📝 Analyzing filename: ${fileName}`);
    
    // Enhanced filename patterns
    const patterns = [
      { regex: /epsg[_\-\s:]*(\d+)/i, type: 'EPSG' },
      { regex: /utm[_\-\s]*(\d{1,2})[ns]?/i, type: 'UTM' },
      { regex: /zone[_\-\s]*(\d{1,2})/i, type: 'UTM' },
      { regex: /(\d{4,5})_utm/i, type: 'EPSG' },
      { regex: /crs[_\-\s]*(\d+)/i, type: 'EPSG' },
      { regex: /srid[_\-\s]*(\d+)/i, type: 'EPSG' }
    ];
    
    for (const { regex, type } of patterns) {
      const match = fileName.match(regex);
      if (match) {
        const code = match[1];
        if (type === 'UTM') {
          const zone = parseInt(code);
          if (zone >= 1 && zone <= 60) {
            // Assume northern hemisphere for Germany/Europe
            const epsgCode = `EPSG:${32600 + zone}`;
            console.log(`🗺️ Detected UTM Zone ${zone} from filename → ${epsgCode}`);
            return epsgCode;
          }
        } else if (type === 'EPSG' && code.length >= 4) {
          const detectedEpsg = `EPSG:${code}`;
          console.log(`✅ Detected EPSG code from filename: ${detectedEpsg}`);
          return detectedEpsg;
        }
      }
    }
  }
  
  // Fallback to individual file content detection
  console.log(`🔍 Falling back to individual file content analysis...`);
  for (const file of files) {
    const epsg = await detectEpsgCode(file);
         if (epsg !== "EPSG:5685") { // If not default, we found something
      console.log(`📋 Using EPSG from file ${file.name}: ${epsg}`);
      return epsg;
    }
  }
  
     console.log(`🔄 No EPSG detected from any file, using default DB_REF system: EPSG:5685`);
   return "EPSG:5685"; // DB_REF system - your primary coordinate system
};

export interface SavedAsset {
  assetId: number;
  assetName: string;
  detectedEpsg: string;
  fileSize: string;
  uploadDate: Date;
}

interface BimUploaderProps {
  onAssetSaved?: (asset: SavedAsset) => void; // Callback to save to Formik
  onClose?: () => void; // Callback to close modal
}

const BimUploader = ({ onAssetSaved, onClose }: BimUploaderProps) => {
  const [uploads, setUploads] = useState<AssetUpload[]>([]);
  const [manualEpsg, setManualEpsg] = useState<string>(''); // Manual override option
  const [stagedFiles, setStagedFiles] = useState<File[]>([]); // Files waiting to be uploaded
  const [isUploading, setIsUploading] = useState<boolean>(false); // Upload state

  // Update upload speed and progress
  const updateUploadProgress = (uploadIdx: number, bytesUploaded: number) => {
    setUploads(prev => prev.map((u, i) => {
      if (i !== uploadIdx) return u;
      
      const now = new Date();
      const uploadDuration = u.uploadStartTime ? (now.getTime() - u.uploadStartTime.getTime()) / 1000 : 0;
      const uploadSpeed = uploadDuration > 0 ? bytesUploaded / uploadDuration : 0;
      const uploadPercent = bytesUploaded / u.fileSizeBytes;
      const uploadProgress = 10 + (uploadPercent * 35); // 10% start + 35% for upload (up to 45%)
      
      return {
        ...u,
        bytesUploaded,
        uploadSpeed,
        progress: Math.min(45, uploadProgress),
        uploadStatus: `Uploading... ${formatFileSize(bytesUploaded)} of ${u.fileSize} (${formatSpeed(uploadSpeed)})`
      };
    }));
  };

  // Poll for asset status for all assets in progress
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];
    const tilingDoneTimeouts: NodeJS.Timeout[] = [];

    uploads.forEach((upload, idx) => {
      if (upload.assetId && upload.showLoader && upload.isTiling) {
        const pollAssetStatus = async () => {
          try {
            const response = await fetch(`https://api.cesium.com/v1/assets/${upload.assetId}`, {
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Accept': 'application/json'
              }
            });
            if (!response.ok) throw new Error(`Status check failed: ${response.statusText}`);
            const data = await response.json();
            
            const processingProgress = 52 + ((data.percentComplete || 0) * 0.48); // Processing is 48% of total (52% to 100%)
            
            setUploads(prev => prev.map((u, i) => i === idx ? {
              ...u,
              tilingStatus: data.status,
              isTiling: true,
              tilingPercent: data.percentComplete || 0,
              progress: processingProgress,
              uploadStatus: `Processing 3D tiles... ${data.percentComplete || 0}% complete`,
              currentPhase: 'processing'
            } : u));
            
            if (data.status === 'COMPLETE') {
              clearInterval(intervals[idx]);
              // Immediately update to complete state when processing is done
              const completedUpload = uploads[idx];
              setUploads(prev => prev.map((u, i) => i === idx ? {
                ...u,
                showLoader: false,
                isTiling: false,
                progress: 100,
                uploadStatus: 'Upload and processing completed successfully!',
                currentPhase: 'complete'
              } : u));
              
              // Save asset to Formik and close modal
              if (completedUpload && onAssetSaved) {
                const savedAsset: SavedAsset = {
                  assetId: completedUpload.assetId!,
                  assetName: completedUpload.assetName,
                  detectedEpsg: completedUpload.detectedEpsg || 'EPSG:5685',
                  fileSize: completedUpload.fileSize,
                  uploadDate: new Date()
                };
                
                console.log(`✅ Saving asset to Formik:`, savedAsset);
                onAssetSaved(savedAsset);
                
                // Close modal after short delay to show success
                setTimeout(() => {
                  onClose?.();
                }, 2000);
              }
            } else if (data.status === 'DATA_ERROR' || data.status === 'ERROR') {
              clearInterval(intervals[idx]);
              setUploads(prev => prev.map((u, i) => i === idx ? {
                ...u,
                showLoader: false,
                isTiling: false,
                uploadStatus: `Processing failed: ${data.status}`,
                currentPhase: 'error'
              } : u));
            }
          } catch (error) {
            clearInterval(intervals[idx]);
            setUploads(prev => prev.map((u, i) => i === idx ? {
              ...u,
              showLoader: false,
              isTiling: false,
              uploadStatus: 'Status polling failed',
              currentPhase: 'error'
            } : u));
          }
        };
        pollAssetStatus();
        intervals[idx] = setInterval(pollAssetStatus, 3000);
      }
    });
    return () => {
      intervals.forEach(interval => interval && clearInterval(interval));
      tilingDoneTimeouts.forEach(timeout => timeout && clearTimeout(timeout));
    };
  }, [uploads]);

  // Handle file staging (drag & drop)
  const handleFileDrop = async (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    console.log(`📁 Staging ${filesArray.length} file(s) for upload`);
    
    // Add files to staging area (avoid duplicates)
    setStagedFiles(prev => {
      const newFiles = filesArray.filter(newFile => 
        !prev.some(existingFile => 
          existingFile.name === newFile.name && existingFile.size === newFile.size
        )
      );
      return [...prev, ...newFiles];
    });
  };

  // Remove file from staging
  const removeStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Clear all staged files
  const clearStagedFiles = () => {
    setStagedFiles([]);
  };

  // Upload all staged files as one asset
  const handleUploadAll = async () => {
    if (stagedFiles.length === 0) return;
    
    setIsUploading(true);
    const filesArray = stagedFiles;
    
    // First, detect EPSG code from all files
    console.log(`🚀 Starting upload process for ${filesArray.length} file(s) as one asset`);
    let detectedEpsg = await detectEpsgFromMultipleFiles(filesArray);
    
    // Check for manual override
    if (manualEpsg && manualEpsg.trim()) {
      const override = manualEpsg.trim().toUpperCase();
      const epsgOverride = override.startsWith('EPSG:') ? override : `EPSG:${override}`;
      console.log(`🔧 Manual override: ${detectedEpsg} → ${epsgOverride}`);
      detectedEpsg = epsgOverride;
    }
    
    console.log(`🌍 Final coordinate system: ${detectedEpsg}`);
    
    // Create one asset for all files
    const assetName = filesArray.length === 1 
      ? filesArray[0].name.replace(/\.(zip|ifc)$/, '') 
      : `Multi-file-asset-${Date.now()}`;
    const uploadIdx = uploads.length;
    const startTime = new Date();
    const totalFileSizeBytes = filesArray.reduce((sum, file) => sum + file.size, 0);
    const estimatedTotal = estimateUploadTime(totalFileSizeBytes);
    
    setUploads(prev => [...prev, {
      file: filesArray[0], // Use first file as reference
      assetId: null,
      assetName,
      progress: 0,
      showLoader: true,
      tilingStatus: '',
      isTiling: false,
      tilingPercent: 0,
      uploadStatus: `Preparing upload of ${filesArray.length} file(s)...`,
      fileSize: formatFileSize(totalFileSizeBytes),
      fileSizeBytes: totalFileSizeBytes,
      startTime,
      estimatedTotal,
      currentPhase: 'preparing',
      detectedEpsg
    }]);

    try {
      // Step 1: Create asset metadata
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        progress: 5, 
        uploadStatus: `Creating asset with ${detectedEpsg}...` 
      } : u));
      
      const postBody = {
        name: assetName,
        description: '3D model uploaded via DevCenter',
        attribution: 'Uploaded via DevCenter',
        type: '3DTILES',
        options: {
          sourceType: 'BIM_CAD',
          geometryCompression: 'DRACO',
          textureFormat: 'KTX2',
          inputCrs: detectedEpsg
        }
      };
      
      console.log(`📋 Asset creation payload for ${assetName}:`, {
        ...postBody,
        options: { ...postBody.options, inputCrs: detectedEpsg }
      });
      
      const response = await fetch('https://api.cesium.com/v1/assets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postBody)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || response.statusText;
        } catch (e: unknown) {
          errorMessage = errorText || response.statusText;
        }
        setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
          ...u, 
          showLoader: false, 
          uploadStatus: `Failed: ${errorMessage}`,
          currentPhase: 'error'
        } : u));
        setIsUploading(false);
        return;
      }
      
      const data = await response.json();
      if (!data.assetMetadata || !data.assetMetadata.id) {
        setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
          ...u, 
          showLoader: false, 
          uploadStatus: 'Invalid response from server',
          currentPhase: 'error'
        } : u));
        setIsUploading(false);
        return;
      }
      
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        assetId: data.assetMetadata.id, 
        progress: 8, 
        uploadStatus: 'Starting upload...',
        currentPhase: 'uploading',
        uploadStartTime: new Date()
      } : u));

      // Step 2: Upload all files to S3 with progress tracking
      const s3Client = new S3Client({
        region: 'us-east-1',
        credentials: {
          accessKeyId: data.uploadLocation.accessKey,
          secretAccessKey: data.uploadLocation.secretAccessKey,
          sessionToken: data.uploadLocation.sessionToken
        },
        endpoint: data.uploadLocation.endpoint
      });
      
      // Upload each file
      for (let fileIndex = 0; fileIndex < filesArray.length; fileIndex++) {
        const file = filesArray[fileIndex];
        const key = `${data.uploadLocation.prefix}${file.name}`;
        
        setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
          ...u, 
          uploadStatus: `Uploading file ${fileIndex + 1}/${filesArray.length}: ${file.name}...`
        } : u));
        
        // Simulate upload progress for large files
        const fileBuffer = new Uint8Array(await file.arrayBuffer());
        
        if (file.size > 10 * 1024 * 1024) { // Files larger than 10MB
          const chunks = Math.ceil(file.size / (2 * 1024 * 1024)); // 2MB chunks
          for (let i = 0; i < chunks; i++) {
            const fileProgress = ((i + 1) / chunks) * file.size;
            const totalProgress = 8 + ((fileIndex * 100 + (fileProgress / file.size) * 100) / filesArray.length) * 0.37; // 37% for upload
            setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
              ...u, 
              progress: Math.min(45, totalProgress)
            } : u));
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        const uploadParams = {
          Bucket: data.uploadLocation.bucket,
          Key: key,
          Body: fileBuffer,
          ContentType: file.type || 'application/octet-stream'
        };
        
        try {
          const command = new PutObjectCommand(uploadParams);
          await s3Client.send(command);
        } catch (error) {
          setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
            ...u, 
            showLoader: false, 
            uploadStatus: `Upload failed on file ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            currentPhase: 'error'
          } : u));
          setIsUploading(false);
          return;
        }
      }
      
      // All files uploaded successfully
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        progress: 48, 
        uploadStatus: 'All files uploaded, finalizing...',
        currentPhase: 'processing'
      } : u));

      // Step 3: Notify completion
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        progress: 50,
        uploadStatus: 'Preparing 3D tile processing...'
      } : u));

      const completeResponse = await fetch(data.onComplete.url, {
        method: data.onComplete.method,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data.onComplete.fields)
      });
      
      if (!completeResponse.ok) {
        setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
          ...u, 
          showLoader: false, 
          uploadStatus: `Completion failed: ${completeResponse.statusText}`,
          currentPhase: 'error'
        } : u));
        setIsUploading(false);
        return;
      }
      
      // Clear staged files and start processing
      setStagedFiles([]);
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        progress: 52,
        uploadStatus: 'Processing 3D tiles...',
        isTiling: true,
        currentPhase: 'processing'
      } : u));
      
    } catch (error) {
      setUploads(prev => prev.map((u, i) => i === uploadIdx ? { 
        ...u, 
        showLoader: false, 
        uploadStatus: `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        currentPhase: 'error'
      } : u));
    } finally {
      setIsUploading(false);
    }
  };

    return (     
        <div className="space-y-4 p-4 overflow-y-auto max-h-[calc(100vh-100px)]">
          {/* Modal Header with Close Button */}
          {onClose && (
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">BIM Upload</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light"
                aria-label="Close"
              >
                ×
              </button>
            </div>
          )}
          
           <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-medium text-blue-900 mb-2">
              Manual Coordinate System Override (optional)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={manualEpsg}
                onChange={(e) => setManualEpsg(e.target.value)}
                placeholder="e.g., 5685, 5514, or EPSG:5685"
                className="flex-1 px-3 py-2 border border-blue-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => setManualEpsg('')}
                className="px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                Clear
              </button>
            </div>
            <p className="text-xs text-blue-600 mt-1">
              Leave empty for automatic detection. Common: 5685 (DB_REF/Germany), 5514 (S-JTSK/Czech)
            </p>
          </div>
          
          <div className="space-y-4">
            <FileDrop
              onDrop={handleFileDrop}
              text={`Drop your BIM/CAD files here to stage them`}
              icon="upload"
              fileTypes={['application/zip', 'application/octet-stream', 'model/gltf-binary', 'model/gltf+json', '.ifc', '.prj', '.xml']}
              fileTypesText={`BIM/CAD files (include .prj, .xml or other georeferencing files if available)`}
              className="h-32"
              multiple
            />
            
            {/* Staged Files */}
            {stagedFiles.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Staged Files ({stagedFiles.length})
                  </h3>
                  <button
                    onClick={clearStagedFiles}
                    className="text-sm text-gray-500 hover:text-red-600"
                  >
                    Clear All
                  </button>
                </div>
                
                <div className="space-y-2 mb-4">
                  {stagedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div className="flex-1">
                        <div className="font-medium text-sm">{file.name}</div>
                        <div className="text-xs text-gray-500">{formatFileSize(file.size)}</div>
                      </div>
                      <button
                        onClick={() => removeStagedFile(index)}
                        className="ml-2 text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Total size: {formatFileSize(stagedFiles.reduce((sum, file) => sum + file.size, 0))}
                  </div>
                  <button
                    onClick={handleUploadAll}
                    disabled={isUploading}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      isUploading 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isUploading ? 'Uploading...' : `Upload All as One Asset`}
                  </button>
                </div>
              </div>
            )}
            
            {uploads.map((upload, idx) => (
              <div key={idx} className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                {/* File Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-gray-900">{upload.assetName}</h3>
                    <p className="text-sm text-gray-500">{upload.fileSize}</p>
                  </div>
                  {upload.uploadSpeed && (
                    <div className="text-right">
                      <p className="text-sm font-medium text-blue-600">{formatSpeed(upload.uploadSpeed)}</p>
                      <p className="text-xs text-gray-500">Upload speed</p>
                    </div>
                  )}
                </div>

                {/* Asset ID and EPSG Info */}
                {upload.assetId && (
                  <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium">Asset ID:</span> {upload.assetId}
                      </div>
                      {upload.detectedEpsg && (
                        <div className="text-blue-600 font-medium">
                          📍 {upload.detectedEpsg}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Dynamic Progress Bar */}
                {upload.showLoader && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">{upload.uploadStatus}</span>
                      <span className="text-sm text-gray-500">{Math.round(upload.progress)}%</span>
                    </div>
                    
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-3 rounded-full transition-all duration-500 ease-out ${
                          getProgressBarColor(upload.fileSizeBytes, upload.uploadSpeed)
                        } ${getProgressAnimation(upload.fileSizeBytes, upload.currentPhase)}`}
                        style={{ 
                          width: `${upload.progress}%`,
                          background: upload.currentPhase === 'uploading' && upload.fileSizeBytes > 50 * 1024 * 1024 
                            ? 'linear-gradient(90deg, #3b82f6, #1d4ed8, #3b82f6)' 
                            : undefined,
                          backgroundSize: upload.currentPhase === 'uploading' ? '200% 100%' : undefined,
                          animation: upload.currentPhase === 'uploading' && upload.fileSizeBytes > 50 * 1024 * 1024 
                            ? 'gradient-shift 2s ease-in-out infinite' 
                            : undefined
                        }}
                      ></div>
                    </div>

                    {/* Progress Details */}
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>
                        {upload.currentPhase === 'preparing' && '🔄 Preparing...'}
                        {upload.currentPhase === 'uploading' && '⬆️ Uploading...'}
                        {upload.currentPhase === 'processing' && '⚙️ Processing...'}
                      </span>
                      {upload.estimatedTotal && upload.currentPhase !== 'processing' && (
                        <span>Est. {Math.round(upload.estimatedTotal)}s total</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Final Status */}
                {!upload.showLoader && (
                  <div className={`p-3 rounded-lg text-sm ${
                    upload.currentPhase === 'complete' 
                      ? 'bg-green-50 text-green-800 border border-green-200' 
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {upload.currentPhase === 'complete' ? '✅' : '❌'} {upload.uploadStatus}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CSS for gradient animation */}
          <style>{`
            @keyframes gradient-shift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
      </div>
  );
};

export default BimUploader;
